import type { RecipientGift } from '@consumer/types/serializers'
import { findGreetingCardFont } from '@shared/greetingCards/fontDefinitions'
import { DEFAULT_GREETING_CARD_CUSTOMIZATION as DEFAULTS } from '@shared/greetingCards/defaultCustomization'
import { contrastTextColor } from '@shared/greetingCards/contrastTextColor'

// Resolves a greeting card's customization (admin overrides + shared
// defaults) into the CSS variables the static-card flow cascades to every
// child (envelope, reading stage, folio). Each field falls back to
// `DEFAULTS` so an unconfigured card still has a complete palette /
// typography — no `var(--x, hardcoded)` fallbacks needed downstream.
// --font-color is mirrored into --text-dark so components reading the
// generic var pick it up too. Shared by StaticAnimatedPage (recipient
// flow), GiftShowcaseFlow (demo/preview), and the admin card preview.
export function useCardCustomizationStyle (giftGetter: () => RecipientGift | undefined) {
  return computed(() => {
    const card = toValue(giftGetter)?.greeting?.card
    const envColor = card?.envelopeColor || DEFAULTS.envelopeColor
    const bgColor = card?.backgroundColor || DEFAULTS.backgroundColor
    const pageBgColor = card?.bookPageColor || DEFAULTS.pageBackgroundColor
    const fontColor = card?.fontColor || DEFAULTS.fontColor
    const font = findGreetingCardFont(card?.fontType) ?? DEFAULTS.font
    return {
      '--env-color': envColor,
      '--env-text-color': contrastTextColor(envColor),
      '--bg-color': bgColor,
      '--page-bg-color': pageBgColor,
      '--font-color': fontColor,
      '--text-dark': fontColor,
      // Quote the family so multi-word names ("Grand Hotel",
      // "Special Gothic Expanded One") parse as a single identifier.
      '--card-font-family': `"${font.family}"`,
      '--card-font-weight': String(font.weight),
      '--card-font-size': font.fontSize,
      '--card-line-height': font.lineHeight,
    }
  })
}
